import React, { useEffect, useState } from "react";
import { Container, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, FormControl, InputLabel, Pagination, Alert, CircularProgress } from "@mui/material";
import { useAuth } from "../../../context/AuthContext";

const LOG_TYPES = [
    { value: "finance", label: "Finanzas", allowedRoles: ["SUPER_ADMIN", "BUSINESS_ADMIN", "ADMIN"] },
    { value: "audit", label: "Auditoría", allowedRoles: ["SUPER_ADMIN", "BUSINESS_ADMIN", "ADMIN"] },
    { value: "opportunities", label: "Oportunidades", allowedRoles: ["SUPER_ADMIN", "BUSINESS_ADMIN", "ADMIN"] },
];

const LOG_FILES = {
    finance: ["transactions", "balance", "payout"],
    audit: ["actions", "access"],
    opportunities: ["changes", "access"],
};

const PAGE_SIZE = 50;

export default function BusinessLogs() {
    const { userProfile } = useAuth();
    const [logType, setLogType] = useState(LOG_TYPES[0].value);
    const [logFile, setLogFile] = useState(LOG_FILES[LOG_TYPES[0].value][0]);
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const allowedTypes = LOG_TYPES.filter(t => t.allowedRoles.includes(userProfile?.role));

    useEffect(() => {
        if (!allowedTypes.some(t => t.value === logType)) return;
        setLogFile(LOG_FILES[logType][0]);
    }, [logType, allowedTypes]);

    useEffect(() => {
        if (!allowedTypes.some(t => t.value === logType)) return;
        setLoading(true);
        setError("");
        fetch(`/api/admin/logs?type=${logType}&file=${logFile}&page=${page}&pageSize=${PAGE_SIZE}`)
            .then(res => {
                if (!res.ok) throw new Error("Error al obtener logs");
                return res.json();
            })
            .then(data => {
                setLogs(data.logs || []);
                setTotal(data.total || 0);
            })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [logType, logFile, page, allowedTypes]);

    if (!allowedTypes.length) {
        return <Alert severity="error">No tienes permisos para ver los logs de negocio.</Alert>;
    }

    return (
        <Container maxWidth="lg">
            <Box my={3}>
                <Typography variant="h5">Logs de Negocio</Typography>
                <Box display="flex" gap={2} my={2}>
                    <FormControl>
                        <InputLabel>Tipo de Log</InputLabel>
                        <Select
                            value={logType}
                            onChange={e => setLogType(e.target.value)}
                        >
                            {allowedTypes.map(type => (
                                <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <InputLabel>Archivo</InputLabel>
                        <Select
                            value={logFile}
                            onChange={e => setLogFile(e.target.value)}
                        >
                            {LOG_FILES[logType].map(file => (
                                <MenuItem key={file} value={file}>{file}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                {error && <Alert severity="error">{error}</Alert>}
                {loading ? <CircularProgress /> : (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Timestamp</TableCell>
                                    <TableCell>Acción</TableCell>
                                    <TableCell>Usuario</TableCell>
                                    <TableCell>Entidad</TableCell>
                                    <TableCell>Detalles</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.map((log, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell>{log.timestamp}</TableCell>
                                        <TableCell>{log.action || "-"}</TableCell>
                                        <TableCell>{log.userId || log.user || "-"}</TableCell>
                                        <TableCell>{log.entityType || "-"}</TableCell>
                                        <TableCell>
                                            <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(log, null, 2)}</pre>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <Box my={2} display="flex" justifyContent="center">
                    <Pagination
                        count={Math.ceil(total / PAGE_SIZE)}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                    />
                </Box>
            </Box>
        </Container>
    );
} 